import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { TFunction } from 'i18next';
import FormsRow from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import FormSelect, { SelectOption } from '../../../../components/molecules/Form/components/FormSelect/FormSelect';
import { FormResponseErrorType, FormUtils, useFormContext } from '../../../../components/molecules/Form';
import { useSonTranslation } from '../../../../translation/useSonTranslation';

import { Assignment } from '../PageToPageAssignmentCard';
import IUrl from '../../../../types/IUrl';
import {
  GetAllAllowedRolesResponse,
  normalizePageToPageOption,
  PageToPageRoleFormOption,
  templateUrl,
  updateAssignmentWithNewRole,
} from '../PageToPage.utils';
import IForm from '../../../../types/IForm';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { PageToPageCreateEditCommonTemplate } from './PageToPageCreateEditCommonTemplate';
import { IOption } from '../../../../types/IOption';

type PageToPageEditFormTemplateProps = {
  templatedPreview: IUrl;
  templatedGetAllAllowedRoles?: IUrl;
  form: IForm;
  pageId: string;
};

export function PageToPageEditFormTemplate({
  templatedPreview,
  templatedGetAllAllowedRoles,
  form,
  pageId,
}: PageToPageEditFormTemplateProps) {
  const { t } = useSonTranslation('PageToPage');

  const [selectedRole, setSelectedRole] = useState<string | undefined>(form.params?.at(0)?.value);
  const [assignmentPreview, setAssignmentPreview] = useState<Assignment | undefined>(undefined);
  const [allAllowedRoles, setAllAllowedRoles] = useState<SelectOption[]>();

  const { submitActionEnabled, getStatus, inputs, onChange } = useFormContext();

  // request preview of a single item that will be displayed in the block in default view.
  // need to ask backend, as the suggest route does not contain all data (no color or category)
  const requestPreviewAssigment = useCallback(
    (roleId: string) => {
      axios
        .get(templateUrl({ url: templatedPreview.url, roleId }))
        .then((response) => {
          setAssignmentPreview(response.data);
        })
        .catch();
    },
    [templatedPreview],
  );

  /**
   * Fired inside useEffect to filter the connection options / roles depending on the response from requesting the
   * backend at "/endpoint/pageToPageGetAllowedRoles".
   */
  const requestGetAllAllowedRolesAndFilterFormSelectOptions = useCallback(() => {
    if (templatedGetAllAllowedRoles) {
      axios
        .get<GetAllAllowedRolesResponse>(
          templateUrl({
            url: templatedGetAllAllowedRoles.url,
            pageId,
          }),
        )
        .then((response) => {
          const newAllowedRoles = normalizePageToPageOption(
            inputs.roleId.options as PageToPageRoleFormOption[] | undefined,
            response.data as unknown as string[] | undefined,
            t as TFunction,
          );

          setAllAllowedRoles(newAllowedRoles);
        })
        .catch(() => {
          setAllAllowedRoles([]);
        });
    }
  }, [inputs.roleId.options, pageId, t, templatedGetAllAllowedRoles]);

  const statusMessage = {
    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('status.edit.success')),
    [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t('status.edit.fail')),
    [FormResponseErrorType.DUPLICATED_ID]: FormUtils.createStatusMessage(t('status.duplicateId')),
  };

  /**
   * fired when a role is selected manually and no preview is given yet!
   * and when entering the edit form -> to initially show the preview card
   */
  useEffect(() => {
    if (selectedRole && !assignmentPreview) {
      requestPreviewAssigment(selectedRole);
      requestGetAllAllowedRolesAndFilterFormSelectOptions();
    }
  }, [assignmentPreview, requestGetAllAllowedRolesAndFilterFormSelectOptions, requestPreviewAssigment, selectedRole]);

  /**
   * To avoid multiple requests, we set the role via state here if we already have an assignmentPreview fetched.
   */
  const updateAssignmentPreview = (roleId: string, options: IOption[] | undefined) => {
    setAssignmentPreview(assignment =>
      assignment ? updateAssignmentWithNewRole(assignment, options, roleId) : assignment,
    );
  };

  return (
    <>
      <FormsRow>
        <FormSelect
          modifier="grouped-stripes"
          id={inputs.roleId.name}
          name={inputs.roleId.name}
          required={inputs.roleId.required}
          value={inputs.roleId.value}
          labelTitle={t('connectionType')}
          // edit form always allows bidirectional options, else this would be filtered by backend
          options={allAllowedRoles}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSelectedRole(event.target.value);
            onChange(event.target.name, event.target.value);
            updateAssignmentPreview(event.target.value, inputs.roleId.options);
          }}
        />
      </FormsRow>
      <PageToPageCreateEditCommonTemplate
        getStatus={getStatus}
        assignmentPreview={assignmentPreview}
        statusMessages={statusMessage}
        submitActionEnabled={submitActionEnabled}
      />
    </>
  );
}
